import { useApolloClient } from '@apollo/client';
import {
  type Column,
  Divider,
  DynamicDataList,
  FormElementContainer,
  FormGroup,
  ReadOnlyField,
} from '@axinom/mosaic-ui';
import { useFormikContext } from 'formik';
import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  ServiceAccountsDocument,
  type ServiceAccountsQuery,
  type ServiceAccountsQueryVariables,
} from '../../../../generated/id-service-env-administration';
import classes from './ServiceAccountImportPreview.module.scss';
import {
  conflictResolutionRenderer,
  conflictsCountRenderer,
  importActionRenderer,
  protectionRenderer,
} from './ServiceAccountImportPreview.renderers';
import {
  ConflictResolutions,
  ImportActions,
  type ImportFormData,
  type ImportPreview,
  type ImportSummary,
  type IncomingServiceAccount,
} from './ServiceAccountImportPreview.types';
import { parseServiceAccountsExportFile } from './ServiceAccountsImport.utils';

type ExistingServiceAccount = NonNullable<
  ServiceAccountsQuery['filtered']
>['nodes'][number];

export const ServiceAccountImportPreview: React.FC = () => {
  const { envId } = useParams<{ envId: string }>();
  const apolloClient = useApolloClient();

  const [incomingAccounts, setIncomingAccounts] = useState<
    IncomingServiceAccount[] | null
  >(null);
  const [existingAccounts, setExistingAccounts] = useState<
    ExistingServiceAccount[]
  >([]);
  const { values, setFieldError, setFieldValue } =
    useFormikContext<ImportFormData>();

  useEffect(() => {
    let cancelled = false;
    const fileValue = values.serviceAccountsJson;

    if (!fileValue?.file) {
      setIncomingAccounts(null);
      setExistingAccounts([]);
      return;
    }

    (async () => {
      const parsed = await parseServiceAccountsExportFile(fileValue.file);

      if (cancelled) {
        return;
      }

      if (!parsed) {
        setIncomingAccounts(null);
        setExistingAccounts([]);
        setFieldError(
          'serviceAccountsJson',
          'Selected file is not a valid service accounts export.',
        );
        return;
      }

      setIncomingAccounts(parsed.serviceAccounts);

      const allExistingAccounts: ExistingServiceAccount[] = [];
      let after: string | undefined;
      let hasNextPage = true;

      while (hasNextPage && !cancelled) {
        const result = await apolloClient.query<
          ServiceAccountsQuery,
          ServiceAccountsQueryVariables
        >({
          query: ServiceAccountsDocument,
          variables: { condition: { environmentId: envId }, after },
          fetchPolicy: 'network-only',
        });

        allExistingAccounts.push(...(result.data.filtered?.nodes ?? []));
        hasNextPage = result.data.filtered?.pageInfo.hasNextPage ?? false;
        after = result.data.filtered?.pageInfo.endCursor ?? undefined;
      }

      if (!cancelled) {
        setExistingAccounts(allExistingAccounts);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [values.serviceAccountsJson, setFieldError, apolloClient, envId]);

  const { importPreview, importSummary } = useMemo(() => {
    const existingProtectionByName = new Map(
      existingAccounts.map((account) => [account.name, account.isProtected]),
    );

    const importPreviewInternal: ImportPreview[] = incomingAccounts
      ? incomingAccounts.map((account) => {
          const previewRow: ImportPreview = {
            name: account.name,
            isProtected: account.isProtected,
            importAction: ImportActions.Create,
          };

          const existingIsProtected = existingProtectionByName.get(
            account.name,
          );
          if (existingIsProtected !== undefined) {
            const conflictResolution = values.conflictResolution;
            switch (conflictResolution) {
              case ConflictResolutions.NotSelected: {
                previewRow.importAction = ImportActions.Conflict;
                break;
              }

              case ConflictResolutions.Skip: {
                previewRow.importAction = ImportActions.Skip;
                break;
              }

              case ConflictResolutions.OverwriteAll: {
                previewRow.importAction = ImportActions.Overwrite;
                break;
              }

              case ConflictResolutions.OverwriteNonProtected: {
                // Whether the existing account can be overwritten depends on its
                // current protection state in the DB, not the file's isProtected value.
                previewRow.importAction = existingIsProtected
                  ? ImportActions.Skip
                  : ImportActions.Overwrite;
              }
            }
          }
          return previewRow;
        })
      : [];

    const importSummaryInternal: ImportSummary[] = [
      {
        id: 'summary',
        detectedItems: incomingAccounts?.length ?? 0,
        conflicts:
          importPreviewInternal.filter(
            (prevRow) => prevRow.importAction !== ImportActions.Create,
          ).length ?? 0,
      },
    ];

    return {
      importPreview: importPreviewInternal,
      importSummary: importSummaryInternal,
    };
  }, [values.conflictResolution, existingAccounts, incomingAccounts]);

  const hasUnresolvedConflicts = importPreview.some(
    (row) => row.importAction === ImportActions.Conflict,
  );
  const hasNothingToImport =
    incomingAccounts !== null &&
    importPreview.every((row) => row.importAction === ImportActions.Skip);

  // Kept in sync on the form as one combined field so the validation
  // schema's cross-field checks on `conflictResolution` can read them via
  // `this.parent`. Combining them into a single field means a single
  // setFieldValue call, so there's no second field/effect it could race
  // against for the same validation pass.
  useEffect(() => {
    void setFieldValue(
      'conflictState',
      { hasUnresolvedConflicts, hasNothingToImport },
      true,
    );
  }, [hasUnresolvedConflicts, hasNothingToImport, setFieldValue]);

  const summaryTableColumns: Column<ImportSummary>[] = [
    {
      propertyName: 'detectedItems',
      label: 'Detected Items',
      sortable: false,
    },
    {
      propertyName: 'conflicts',
      label: 'Conflicts',
      render: conflictsCountRenderer,
      sortable: false,
    },
    {
      label: 'Conflict Resolution',
      key: 'unbound',
      sortable: false,
      render: conflictResolutionRenderer,
    },
  ];
  const previewTableColumns: Column<ImportPreview>[] = [
    {
      propertyName: 'name',
      label: 'Name',
    },
    {
      propertyName: 'isProtected',
      label: 'Protection',
      render: protectionRenderer,
      sortable: false,
    },
    {
      propertyName: 'importAction',
      label: 'Import Action',
      sortable: false,
      render: importActionRenderer,
    },
  ];

  return (
    <FormGroup title="Import Preview & Validation">
      {incomingAccounts ? (
        <>
          <FormElementContainer label="Import Summary">
            <DynamicDataList<ImportSummary>
              columns={summaryTableColumns}
              value={importSummary}
              allowReordering={false}
              positionPropertyName="id"
            />
          </FormElementContainer>
          <Divider />
          <FormElementContainer label="Import Preview">
            <div className={classes.previewListContainer}>
              <DynamicDataList<ImportPreview>
                columns={previewTableColumns}
                value={importPreview}
                allowReordering={false}
              />
            </div>
          </FormElementContainer>
        </>
      ) : (
        <ReadOnlyField label="Import Summary" value="No Data" />
      )}
    </FormGroup>
  );
};
